import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Palette, Globe, Sparkles, Users } from 'lucide-react';
import { useCanvasMode, type CanvasMode } from '@/contexts/CanvasModeContext';

interface CanvasModeDialogProps {
  open: boolean;
  onClose: () => void;
}

export const CanvasModeDialog: React.FC<CanvasModeDialogProps> = ({ open, onClose }) => {
  const { setMode } = useCanvasMode();

  const handleSelectMode = (mode: CanvasMode) => {
    setMode(mode);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center mb-2">
            Welcome to Canvas! 🎨
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            Choose how you want to use your canvas
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {/* Composer Mode */}
          <button
            onClick={() => handleSelectMode('composer')}
            className="group relative overflow-hidden rounded-lg border-2 border-border hover:border-primary transition-all p-6 text-left bg-gradient-to-br from-purple-500/10 to-pink-500/10 hover:from-purple-500/20 hover:to-pink-500/20"
          >
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <Palette className="h-6 w-6 text-purple-500" />
                </div>
                <h3 className="text-xl font-bold">Composer Mode</h3>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                Create content for social media without going online
              </p>

              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Sparkles className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span className="text-xs">Works completely offline</span>
                </div>
                <div className="flex items-start gap-2">
                  <Sparkles className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span className="text-xs">Export as image for sharing</span>
                </div>
                <div className="flex items-start gap-2">
                  <Sparkles className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span className="text-xs">No uploads or storage used</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-border/50">
                <span className="text-xs font-medium text-purple-500">
                  Perfect for quick compositions
                </span>
              </div>
            </div>
          </button>

          {/* Online Mode */}
          <button
            onClick={() => handleSelectMode('online')}
            className="group relative overflow-hidden rounded-lg border-2 border-border hover:border-primary transition-all p-6 text-left bg-gradient-to-br from-blue-500/10 to-cyan-500/10 hover:from-blue-500/20 hover:to-cyan-500/20"
          >
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Globe className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="text-xl font-bold">Online Mode</h3>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                Collaborate in real-time with other users
              </p>

              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Users className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-xs">Real-time collaboration</span>
                </div>
                <div className="flex items-start gap-2">
                  <Users className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-xs">See other users' cursors</span>
                </div>
                <div className="flex items-start gap-2">
                  <Users className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-xs">Auto-save to the cloud</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-border/50">
                <span className="text-xs font-medium text-blue-500">
                  Perfect for collaboration
                </span>
              </div>
            </div>
          </button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground">
            You can switch modes anytime from the canvas toolbar
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
